import { put, select, takeLatest } from '@redux-saga/core/effects';
import { apiCall, APIValidationError } from '../api';
import { Await, Result } from '../api.d';
import { baseURL } from '../root-saga';
import {
  BidDTO,
  BidState,
  ConflictDTO,
  CreateSubsetBid,
  GetBidOptionsResponse,
  GetBidsResponse,
  PostBidResponse,
} from './types';
import errorSlice from '../error/slice';
import bidSlice from './slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { FileUploadUpdaterDTO, UploadFeedbackResponse } from '../submission/types';
import { selectBidState } from '../selectors';

const getBidOptions = async (): Promise<Result<GetBidOptionsResponse, APIValidationError>> => {
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };

  return apiCall<GetBidOptionsResponse>(`${baseURL}/api/bids/options`, init);
};

function* getBidOptionsSaga() {
  const result = (yield getBidOptions()) as Await<ReturnType<typeof getBidOptions>>;

  switch (result.type) {
    case 'ok':
      yield put(bidSlice.actions['GET:BID_OPTIONS:OK'](result.value));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    default:
      break;
  }
  return;
}

const getOwnBids = async (): Promise<Result<GetBidsResponse, APIValidationError>> => {
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };

  return apiCall<GetBidsResponse>(`${baseURL}/api/bids/own`, init);
};

function* getOwnBidsSaga() {
  const result = (yield getOwnBids()) as Await<ReturnType<typeof getOwnBids>>;

  switch (result.type) {
    case 'ok':
      yield put(bidSlice.actions['GET:BIDS:OWN:OK'](result.value));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    default:
      break;
  }
  return;
}

const postBid = async (data: BidDTO): Promise<Result<PostBidResponse, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify(data),
  };

  return apiCall<PostBidResponse>(`${baseURL}/api/bids`, init);
};

function* postBidSaga(action: PayloadAction<BidDTO>) {
  /* Keep previous state, just in case there's an error... */
  const bidState: BidState = yield select(selectBidState);
  const current = bidState.byId[action.payload.bidable_id];

  /* Do an optimistic update to improve UI responsiveness */
  yield put(bidSlice.actions['POST:BID:OK'](action.payload));
  const result = (yield postBid(action.payload)) as Await<ReturnType<typeof postBid>>;

  switch (result.type) {
    case 'ok':
      break;
    case 'validation-error':
      yield put(
        bidSlice.actions['POST:BID:OK']({
          bidable_id: action.payload.bidable_id,
          bid_option_id: current.bid_option_id, // Reverting bid option....
        }),
      );
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    default:
      break;
  }
  return;
}

const postConflict = async (data: ConflictDTO): Promise<Result<{ data: any }, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify(data),
  };

  return apiCall<{ data: any }>(`${baseURL}/api/conflicts`, init);
};

function* postConflictSaga(data: PayloadAction<ConflictDTO>) {
  /* Do an optimistic update to improve UI responsiveness */
  yield put(bidSlice.actions['POST:CONFLICT:OK'](data.payload));

  const result = (yield postConflict(data.payload)) as Await<ReturnType<typeof postConflict>>;

  switch (result.type) {
    case 'ok':
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    default:
      break;
  }
  return;
}

export const postSubsetBids = async (
  data: FileUploadUpdaterDTO,
): Promise<Result<UploadFeedbackResponse, APIValidationError>> => {
  const { file, dry_run } = data;

  const formData = new FormData();
  formData.append('file', file);

  const init = {
    method: 'POST',
    auth: true,
    role: true,
    defaultContentType: true,
    body: formData,
  };

  return apiCall<UploadFeedbackResponse>(`${baseURL}/api/bidding/subset/bulk?dry_run=${dry_run ? 1 : 0}`, init);
};

const createSubset = async (data: CreateSubsetBid): Promise<Result<{ data: any }, APIValidationError>> => {
  const { person_role_id, ...rest } = data;
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify(rest),
  };

  return apiCall<{ data: any }>(`${baseURL}/api/bidding/subset/${person_role_id}`, init);
};

function* createSubsetSaga(data: PayloadAction<CreateSubsetBid>) {
  const result = (yield createSubset(data.payload)) as Await<ReturnType<typeof createSubset>>;

  switch (result.type) {
    case 'ok':
      yield put(errorSlice.actions['OPEN:SNACKBAR']({ message: 'Subset bid created.', severity: 'success' }));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    default:
      break;
  }
  return;
}

export default [
  takeLatest(bidSlice.actions['GET:BID_OPTIONS'], getBidOptionsSaga),
  takeLatest(bidSlice.actions['GET:BIDS:OWN'], getOwnBidsSaga),
  takeLatest(bidSlice.actions['POST:BID'], postBidSaga),
  takeLatest(bidSlice.actions['POST:CONFLICT'], postConflictSaga),
  takeLatest(bidSlice.actions['CREATE_SUBSET'], createSubsetSaga),
];
